import React from 'react';
import { FEATURE_FLAGS } from '../../../engine/scoring/enhancedProfile';

const EnhancedScoreDisplay = ({ scores, className = "" }) => {
  if (!FEATURE_FLAGS.ADVANCED_SCORING || !scores.enhanced) {
    return null;
  }

  const { confidence, adaptive, crossDimensional, development } = scores;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Confidence Intervals */}
      {confidence && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Score Confidence</h3>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              confidence.level === 'high' ? 'bg-green-100 text-green-800' :
              confidence.level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {confidence.level.toUpperCase()} ({Math.round(confidence.overall * 100)}%)
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">{confidence.sampleSize}</div>
              <div className="text-sm text-gray-600">Questions Answered</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">±{confidence.margin}</div>
              <div className="text-sm text-gray-600">Margin of Error</div>
            </div>
          </div>
        </div>
      )}

      {/* Adaptive Metrics */}
      {adaptive && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Response Analytics</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Response Consistency</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${adaptive.responseConsistency * 100}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{Math.round(adaptive.responseConsistency * 100)}%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Engagement Level</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${adaptive.engagementLevel * 100}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{Math.round(adaptive.engagementLevel * 100)}%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Cognitive Load</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{ width: `${adaptive.cognitiveLoad * 100}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{Math.round(adaptive.cognitiveLoad * 100)}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cross-Dimensional Insights */}
      {crossDimensional && crossDimensional.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cross-Dimensional Insights</h3>
          
          <div className="space-y-4">
            {crossDimensional.map((insight, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${
                insight.type === 'synergy' ? 'bg-green-50 border-green-400' :
                insight.type === 'potential' ? 'bg-blue-50 border-blue-400' :
                insight.type === 'balance' ? 'bg-purple-50 border-purple-400' :
                'bg-yellow-50 border-yellow-400'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">{insight.title}</h4>
                    <p className="text-sm text-gray-700">{insight.description}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {insight.categories.map(cat => (
                        <span key={cat} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {cat.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    insight.strength === 'high' ? 'bg-green-100 text-green-800' :
                    insight.strength === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {insight.strength.toUpperCase()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Development Path */}
      {development && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Personalized Development Path</h3>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              development.priority === 'high' ? 'bg-red-100 text-red-800' :
              development.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-green-100 text-green-800'
            }`}>
              {development.priority.toUpperCase()} PRIORITY
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
              <ul className="space-y-1">
                {development.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-start">
                    <span className="text-indigo-500 mr-2">•</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Suggested Resources</h4>
              <ul className="space-y-1">
                {development.resources.map((resource, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    {resource}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Recommended Timeframe</span>
              <span className="font-medium">{development.timeframe}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedScoreDisplay;